import type { Translate } from "./i18n";
import type { Lang } from "./types";

/**
 * Pure formatting: dates, durations, scores and language names.
 * No React, so every rule is unit-testable on its own.
 */

const LOCALE: Record<Lang, string> = { sv: "sv-SE", en: "en-GB" };

export function dateTime(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE[lang], {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

export function dateOnly(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE[lang], { dateStyle: "medium" }).format(
    d
  );
}

/** "2 d 4 h", "3 h 12 min", "12 min", or "less than 1 min". */
export function remaining(ms: number, t: Translate): string {
  if (ms < 60_000) return t("time.lessThanMinute");
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return `${hours} h ${mins} min`;
  return `${mins} min`;
}

/**
 * A running time for the title page: "1 t 50 m" / "47 m" in Swedish,
 * "1 h 50 m" / "47 m" in English. Whole minutes, rounded. "" under a minute
 * (the server already omits those - this never prints "0 m").
 */
export function runtime(
  seconds: number | null | undefined,
  lang: Lang
): string {
  if (typeof seconds !== "number" || !Number.isFinite(seconds)) return "";
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hourUnit = lang === "en" ? "h" : "t";
  if (h === 0) return `${m} m`;
  return m === 0 ? `${h} ${hourUnit}` : `${h} ${hourUnit} ${m} m`;
}

/** A 0–10 score with one decimal in the UI language: "7,7" / "7.7". */
export function scoreText(value: number, lang: Lang): string {
  return new Intl.NumberFormat(LOCALE[lang], {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

/** A whole number with the UI language's grouping: "12 345" / "12,345". */
export function count(value: number, lang: Lang): string {
  return new Intl.NumberFormat(LOCALE[lang]).format(value);
}

/**
 * A language code from the catalogue ("sv", "en", "fra") as a name in the
 * UI language ("svenska", "English"); the code itself, upper-cased, when the
 * browser cannot name it.
 */
export function languageName(code: string, lang: Lang): string {
  const c = code.trim();
  if (!c) return "";
  try {
    const names = new Intl.DisplayNames([LOCALE[lang]], { type: "language" });
    const name = names.of(c);
    if (name && name.toLowerCase() !== c.toLowerCase()) {
      return lang === "sv"
        ? name
        : name.charAt(0).toUpperCase() + name.slice(1);
    }
  } catch {
    // An invalid tag throws; fall through to the code.
  }
  return c.toUpperCase();
}
